/* ============================================================================
   REPORTE DE GESTIÓN DEL RIESGO OPERATIVO

   Dos fuentes, y solo dos:

     · Eurocopter IN 2255-I-00, Anexo 2 — el formulario de seguimiento de
       aplicación de las medidas de protección y de gestión del riesgo. Define
       la estructura: evento indeseable, eventos límite relacionados, nivel de
       riesgo inicial y actual, precursores, defensas (control y recuperación),
       responsable, estado de implementación, y las medidas de protección para
       los eventos límite.

     · Metodología bow-tie (SKYbrary/EUROCONTROL) — aporta el peligro de
       origen y los factores de escalada, que el Anexo 2 no pide de forma
       explícita.

   NO se usa ningún otro estándar. En particular, este reporte NO es un informe
   BARS: ese formato lo define el BAR Standard de la Flight Safety Foundation,
   que no está disponible acá. Llamarlo así sería atribuirle una conformidad
   que nadie verificó.

   Este módulo arma el MODELO del reporte, no su presentación. Así se puede
   probar el contenido sin abrir un navegador.
   ========================================================================== */

import { evaluarBowTie } from "./auditoria.js";
import { nivelDeRiesgo, GRAVEDAD, PROBABILIDAD, RANGO_NIVEL, SIGNIFICADO_NIVEL } from "./riesgo.js";

/** Separa un campo de texto libre en ítems. */
function items(texto) {
  if (typeof texto !== "string") return [];
  return texto.split(/\n|;/).map((s) => s.trim()).filter(Boolean);
}

/**
 * Ficha de un evento, con la estructura de las columnas del Anexo 2.
 *
 * Una decisión importante: el Anexo 2 impreso agrupa cada defensa bajo el
 * precursor que combate. La app guarda las defensas como texto libre, sin esa
 * asociación. Repartirlas por posición produciría un documento que parece
 * completo y afirma vínculos que nadie estableció, así que se listan por
 * separado y la ficha declara que la asociación precursor–defensa no está
 * registrada. Un hueco visible es preferible a un vínculo inventado.
 */
export function construirFichaAnexo2(evento = {}, categoriasEu = []) {
  const nivelInicial = nivelDeRiesgo(evento.gravedadInicial, evento.probabilidadInicial);
  const nivelActual = nivelDeRiesgo(evento.gravedadActual, evento.probabilidadActual);
  const bt = evaluarBowTie(evento);

  const etiquetaEu = (clave) => {
    const c = categoriasEu.find((x) => x.key === clave);
    return c ? `${c.key} — ${c.label}` : clave;
  };

  return {
    ref: evento.ref || "",
    eventoIndeseable: evento.descripcion || "(sin describir)",
    revisadoEl: evento.revisadoEl || null,

    // Nudo izquierdo del bow-tie.
    peligro: evento.peligro || null,
    precursores: items(evento.precursores),

    // Nudo derecho.
    eventosLimite: (evento.euCategorias || []).map(etiquetaEu),

    nivelRiesgo: {
      inicial: nivelInicial,
      actual: nivelActual,
      gravedadInicial: GRAVEDAD[evento.gravedadInicial] ?? null,
      probabilidadInicial: PROBABILIDAD[evento.probabilidadInicial] ?? null,
      gravedadActual: GRAVEDAD[evento.gravedadActual] ?? null,
      probabilidadActual: PROBABILIDAD[evento.probabilidadActual] ?? null,
      significadoActual: SIGNIFICADO_NIVEL[nivelActual] || null,
      // Positivo = el riesgo bajó. Es lo que un auditor mira primero.
      reduccion:
        nivelInicial && nivelActual
          ? RANGO_NIVEL[nivelInicial] - RANGO_NIVEL[nivelActual]
          : null,
    },

    defensas: {
      control: items(evento.medidasControl),
      recuperacion: items(evento.medidasRecuperacion),
      mitigacion: items(evento.medidasMitigacion),
      // Declaración explícita: el vínculo precursor–defensa no está registrado.
      asociacionRegistrada: false,
    },

    factoresEscalada: items(evento.factoresEscalada),
    controlDeEscalada: items(evento.controlEscalada),

    responsable: evento.responsable || null,
    implementado: evento.implementado || "No",

    integridad: { porcentaje: bt.porcentaje, faltantes: bt.faltantes.map((f) => f.pregunta) },
  };
}

/** ¿Cae la fecha dentro del período? Un evento sin fecha nunca se descarta. */
function dentroDelPeriodo(fechaISO, desde, hasta) {
  if (!fechaISO) return true;
  const f = new Date(fechaISO);
  if (Number.isNaN(f.getTime())) return true;
  if (desde && f < new Date(desde)) return false;
  if (hasta && f > new Date(`${hasta}T23:59:59`)) return false;
  return true;
}

/**
 * Reporte completo para un período.
 *
 * Los eventos sin fecha de alta se incluyen SIEMPRE y se cuentan aparte. Si se
 * los filtrara por no tener fecha desaparecerían del reporte sin que nadie se
 * entere, que es la peor forma de perder un riesgo.
 */
export function construirReporteSGS({
  eventos = [],
  reportes = [],
  politica = {},
  desde = null,
  hasta = null,
  categoriasEu = [],
  operador = "",
} = {}) {
  // Un registro corrupto —un null en el arreglo por una escritura a medias en
  // IndexedDB, por ejemplo— hacía reventar el reporte entero. Se descartan,
  // pero se cuentan y se avisan: perder un evento en silencio es perder un
  // riesgo de vista.
  const validos = eventos.filter((e) => e && typeof e === "object");
  const corruptos = eventos.length - validos.length;
  const enPeriodo = validos.filter((e) => dentroDelPeriodo(e.creado, desde, hasta));
  const fichas = enPeriodo.map((e) => construirFichaAnexo2(e, categoriasEu));
  const sinFecha = enPeriodo.filter((e) => !e.creado).length;

  const porNivelActual = {};
  const porNivelInicial = {};
  fichas.forEach((f) => {
    if (f.nivelRiesgo.actual) porNivelActual[f.nivelRiesgo.actual] = (porNivelActual[f.nivelRiesgo.actual] || 0) + 1;
    if (f.nivelRiesgo.inicial) porNivelInicial[f.nivelRiesgo.inicial] = (porNivelInicial[f.nivelRiesgo.inicial] || 0) + 1;
  });

  const avisos = [];
  if (corruptos > 0) avisos.push(`${corruptos} entrada(s) del registro estaban corruptas y se descartaron. Revisa el registro del SGS.`);
  if (fichas.length === 0) avisos.push("No hay eventos indeseables en el período seleccionado.");
  if (sinFecha > 0) avisos.push(`${sinFecha} evento(s) sin fecha de alta: se incluyen en todos los períodos para no perderlos de vista.`);
  const incompletas = fichas.filter((f) => f.integridad.porcentaje < 100).length;
  if (incompletas > 0) avisos.push(`${incompletas} ficha(s) con el análisis bow-tie incompleto. El reporte las incluye igual y señala qué falta en cada una.`);
  const sinResponsable = fichas.filter((f) => !f.responsable).length;
  if (sinResponsable > 0) avisos.push(`${sinResponsable} medida(s) sin responsable asignado. El Anexo 2 exige un responsable por cada medida de protección.`);
  const sinReduccion = fichas.filter((f) => f.nivelRiesgo.reduccion === 0).length;
  if (sinReduccion > 0) avisos.push(`${sinReduccion} evento(s) mantienen el mismo nivel de riesgo tras aplicar las defensas.`);
  const empeoraron = fichas.filter((f) => f.nivelRiesgo.reduccion !== null && f.nivelRiesgo.reduccion < 0).length;
  if (empeoraron > 0) avisos.push(`${empeoraron} evento(s) tienen un riesgo actual MAYOR que el inicial. Revisar la clasificación o las defensas.`);

  return {
    encabezado: {
      titulo: "Seguimiento de la gestión del riesgo operativo",
      subtitulo: "Formulario del Anexo 2 — Eurocopter Information Notice N° 2255-I-00, ampliado con la metodología bow-tie (SKYbrary/EUROCONTROL)",
      operador,
      responsableSgs: politica.responsable || null,
      politica: politica.declaracion || null,
      desde, hasta,
      emitido: new Date().toISOString(),
    },
    fichas,
    resumen: {
      eventos: fichas.length,
      corruptos,
      sinFecha,
      implementadas: fichas.filter((f) => f.implementado === "Sí").length,
      parciales: fichas.filter((f) => f.implementado === "Parcial").length,
      sinImplementar: fichas.filter((f) => f.implementado === "No").length,
      sinResponsable,
      integridadMedia: fichas.length
        ? Math.round(fichas.reduce((s, f) => s + f.integridad.porcentaje, 0) / fichas.length)
        : 0,
      reportesAsr: reportes.length,
      asrSinProcesar: reportes.filter((r) => r.estado !== "Convertido a EI").length,
      porNivelInicial,
      porNivelActual,
    },
    avisos,
    // Sello de procedencia. Va impreso: quien reciba el papel tiene que saber
    // contra qué se armó y contra qué NO.
    procedencia:
      "Estructura tomada del Anexo 2 de la Eurocopter IN 2255-I-00 y de la metodología bow-tie publicada por " +
      "SKYbrary/EUROCONTROL. No es un informe BARS ni sigue el BAR Standard de la Flight Safety Foundation. " +
      "Herramienta de apoyo a la gestión: no reemplaza al manual de operaciones, al RFM ni a la autoridad aeronáutica.",
  };
}
